import * as qsLib from 'qs';

import { handlePagerDutyApiResponse } from 'src/lib/api/handlePagerDutyApiResponse';
import { createLogsSocketConnection } from 'src/lib/api/logsSocketConnection';
import { getEnvironment } from 'src/lib/featureFlags';

import BaseApi from './baseApi';
import jira from './jira';

class Integration {
  constructor(jira) {
    this._jira = jira;
    this.baseUrl = getEnvironment().integrationServiceBaseUrl;
    this.pdSession = {};
  }

  configure({ apiBaseUrl, appBaseUrl, accountBaseUrl, accessToken }) {
    this.pdSession = { apiBaseUrl, appBaseUrl, accountBaseUrl, accessToken };
  }

  _request({ method, url, body, headers, qs, interpolateBody = false }) {
    url = `${this.baseUrl}${url}`;

    let query = qsLib.stringify(qs, { arrayFormat: 'brackets' });
    if (query) {
      if (url.includes('?')) url = `${url}&${query}`;
      else url = `${url}?${query}`;
    }

    headers = headers || {};
    if (this.pdSession.accessToken) {
      headers.Authorization = `Bearer ${this.pdSession.accessToken}`;
    }

    headers['X-PagerDuty-Environment'] = JSON.stringify(
      {
        apiBaseUrl: this.pdSession.apiBaseUrl,
        appBaseUrl: this.pdSession.appBaseUrl,
        accountBaseUrl: this.pdSession.accountBaseUrl
      });

    const response = interpolateBody
      ? BaseApi.proxyRequest(method, url, body, headers, interpolateBody)
      : BaseApi.signedRequest(method, url, body, headers);

    return response.then(({ status, responseText }) => handlePagerDutyApiResponse({ headers, url, status, responseText }));
  }

  async getAccountMapping() {
    return this._request({
      method: 'GET',
      url: '/accounts_mapping',
    }).then(res => res && res.accounts_mapping);
  }

  async getOtherConnectedAccounts() {
    return this._request({
      method: 'GET',
      url: '/related_accounts_mappings',
      qs: { integration_type: 'jira_server' }
    });
  }

  async createAccountsMapping(user) {
    return this._request({
      method: 'POST',
      url: '/accounts_mapping',
      interpolateBody: true,
      body: {
        accounts_mapping: {
          jira: {
            shared_secret: '{{settings.sharedSecret}}',
          },
          users_mapping_config: {},
          metadata: {
            created_by_user: user
          },
        },
      }
    }).then(res => res.accounts_mapping);
  }

  async removeAccountsMapping() {
    return this._request({
      method: 'DELETE',
      url: '/accounts_mapping',
    });
  }

  async updateAccountsMapping(body) {
    return this._request({
      method: 'PUT',
      url: '/accounts_mapping',
      body: {
        accounts_mapping: body
      },
    }).then(res => res.accounts_mapping);
  }

  async validatePagerDutyCredentials() {
    return this._request({
      method: 'GET',
      url: '/accounts_mapping/validate_access/pagerduty',
    });
  }

  async validateJiraCredentials() {
    return this._request({
      method: 'GET',
      url: '/accounts_mapping/validate_access/jira',
    });
  }

  async getAllRules() {
    return this._request({
      method: 'GET',
      url: '/rules',
    }).then(res => res);
  }

  async createRule(rule) {
    return this._request({
      method: 'POST',
      url: `/rules`,
      body: { rule }
    }).then(res => res.rule);
  }

  async getRule(id) {
    return this._request({
      method: 'GET',
      url: `/rules/${id}`,
    }).then(res => res.rule);
  }

  async updateRule(id, rule) {
    return this._request({
      method: 'PUT',
      url: `/rules/${id}`,
      body: { rule }
    }).then(res => res.rule);
  }

  async removeRule(id) {
    return this._request({
      method: 'DELETE',
      url: `/rules/${id}`,
    });
  }

  async getMappedUsers(qs = {}) {
    return this._request({
      method: 'GET',
      url: `/user_mappings`,
      qs,
    });
  }

  async createUserMapping(user_mapping) {
    return this._request({
      method: 'POST',
      url: `/user_mappings`,
      body: { user_mapping }
    }).then(res => res.user_mapping);
  }

  async createUserMappingsBatch(user_mappings) {
    return this._request({
      method: 'POST',
      url: `/user_mappings/batch`,
      body: { user_mappings }
    }).then(res => res.user_mapping);
  }

  async getUserMapping(id) {
    return this._request({
      method: 'GET',
      url: `/user_mappings/${id}`,
    }).then(res => res.user_mapping);
  }

  async removeUserMapping(id) {
    return this._request({
      method: 'DELETE',
      url: `/user_mappings/${id}`,
    });
  }

  async getIncidentIssueMappingByIssueKey(issueKey) {
    return this._request({
      url: `/incident_issue_mapping?issue_id=${issueKey}&include[]=incident`,
      method: 'GET',
    });
  }

  async createIncidentIssueMapping(incidentId, issueId) {
    return this._request({
      url: `/incident_issue_mapping`,
      method: 'POST',
      body: { incident_issue_mapping: {
        incident: { id: incidentId },
        issue: { id: issueId }
      } }
    });
  }

  async createIncident(service_id, title, description, issue, jira_user) {
    return this._request({
      url: `/create_incident_from_sidebar`,
      method: 'POST',
      body: { incident: { service_id, title, description, issue, jira_user } },
    });
  }

  async removeIncidentIssueMapping(mappingId) {
    return this._request({
      url: `/incident_issue_mapping/${mappingId}`,
      method: 'DELETE',
    });
  }

  async getIncidentLogEntries(incidentId) {
    return this._request({
      url: `/incident_log_entries?incident_id=${incidentId}`,
      method: 'GET',
    });
  }

  async getIncidentNotes(incidentId) {
    return this._request({
      url: `/incident_notes?incident_id=${incidentId}`,
      method: 'GET',
    });
  }

  async getAssociatedServices(project_id) {
    return this._request({
      url: `/associated_services`,
      qs: { project_id },
      method: 'GET',
    });
  }

  async getAllExpectedWebhooks() {
    return this._request({
      url: `/expected_webhooks`,
      method: 'GET',
    });
  }

  async signWebhook(params) {
    return this._request({
      url: `/webhooks/sign`,
      method: 'POST',
      body: params
    });
  }

  async consumeLogs(accountsMappingId) {
    const baseUrl = this.baseUrl
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');
    return createLogsSocketConnection(baseUrl, this.pdSession.accessToken, accountsMappingId);
  }
}

window.__integration_client = window.__cy_stub_integration_client || new Integration(jira);
export default window.__integration_client;
